import { Button } from '@arcadiapower/shrike';
import React, { useRef, useState } from 'react';
import { Separator } from '~/components/separator';
import { Text, Title } from '~/components/typography';
import { uploadContracts } from '~/services';
import { UploadLabel } from './contract-upload.style';

export const ContractUpload = ({
  userId,
  utilityAccountId,
  contract,
  uniqueKey,
  onUpload,
  disabled,
  onUploadSuccess,
  externalSystem,
}) => {
  const copy = defaultCopy;
  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [result, setResult] = useState<boolean>(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      setFile(files[0]);

      // Read file content as base64
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target && event.target.result) {
          setFileBase64(event.target.result.toString());
        }
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleDelete = () => {
    setFile(null);
    setFileBase64(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input value
    }
  };

  const handleSubmit = async () => {
    setResult(true);
    if (file && fileBase64) {
      onUpload(uniqueKey);
      const data = await uploadContracts({
        userId,
        utilityAccountId,
        externalSystem,
        vnmSolarProjectContracts: [contract],
        file: fileBase64, // Send base64 data to the backend
      });
      if (data) {
        setIsUploadSuccess(true);
        onUpload(uniqueKey); // Notify parent component about successful upload
        onUploadSuccess(uniqueKey);
      }
      setResult(!!data);
    }
  };

  return (
    <div>
      <Title fontWeight={500} fontSize="18px">
        {contract?.displayName}
      </Title>
      <UploadLabel htmlFor={`file-upload-${uniqueKey}`} data-testid="upload-file-button">
        {file ? file.name : 'No file chosen. Click to upload.'}
      </UploadLabel>
      <input
        ref={fileInputRef}
        id={`file-upload-${uniqueKey}`}
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <div>
        {isUploadSuccess && (
          <Text color="accent4" margin="8px">
            {copy.successMessage}
          </Text>
        )}
        <Button
          onClick={handleDelete}
          margin="12px"
          disabled={!file || result}
          mode="destructiveOutline"
          data-testid="delete-button"
        >
          {copy.deleteButton}
        </Button>
        <Button
          mode="primary"
          onClick={handleSubmit}
          margin="6px"
          disabled={!file || result || disabled}
          data-testid="submit-button"
        >
          {copy.uploadButton}
        </Button>
        <Separator padding="0px" marginTop="40px" />
      </div>
    </div>
  );
};

const defaultCopy = {
  uploadButton: 'Upload',
  deleteButton: 'Delete',
  successMessage: 'Contract uploaded successfully.',
};
